export const WHITEBOARD_LINK_TYPES = {
    PRODUCT: 'product',
    CHART: 'chart',
};

function createLinkId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `link-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function buildLinkedByUser(user) {
    if (!user?.uid) return null;
    return {
        userId: user.uid,
        displayName: user.displayName || user.email || 'Anonymous',
        hasAvatar: user.hasAvatar === true,
    };
}

/**
 * Normalize chart links from payload, migrating legacy single associatedProduct when needed.
 */
export function normalizeWhiteboardLinks(whiteboard) {
    const links = Array.isArray(whiteboard?.links)
        ? whiteboard.links.filter((link) => link?.targetId && link?.type)
        : [];

    const legacyProduct = whiteboard?.associatedProduct;
    if (legacyProduct?.id) {
        const alreadyLinked = links.some(
            (link) => link.type === WHITEBOARD_LINK_TYPES.PRODUCT && link.targetId === legacyProduct.id,
        );
        if (!alreadyLinked) {
            links.push({
                id: `legacy-${legacyProduct.id}`,
                type: WHITEBOARD_LINK_TYPES.PRODUCT,
                targetId: legacyProduct.id,
                title: legacyProduct.title || legacyProduct.id,
                productType: legacyProduct.productType || null,
                linkedBy: null,
            });
        }
    }

    return links;
}

export function filterLinksByType(links, type) {
    return (Array.isArray(links) ? links : []).filter((link) => link.type === type);
}

export function buildProductLink(option, user) {
    if (!option?.id) return null;
    return {
        id: createLinkId(),
        type: WHITEBOARD_LINK_TYPES.PRODUCT,
        targetId: option.id,
        title: option.title || option.id,
        productType: option.productType || null,
        linkedBy: buildLinkedByUser(user),
        linkedAt: new Date().toISOString(),
    };
}

export function buildChartLink(chart, user) {
    if (!chart?.id) return null;
    return {
        id: createLinkId(),
        type: WHITEBOARD_LINK_TYPES.CHART,
        targetId: chart.id,
        title: chart.title || chart.id,
        linkedBy: buildLinkedByUser(user),
        linkedAt: new Date().toISOString(),
    };
}

export function addWhiteboardLinkToList(links, link) {
    if (!link?.targetId || !link?.type) return links;
    const existing = Array.isArray(links) ? links : [];
    const duplicate = existing.some(
        (entry) => entry.type === link.type && entry.targetId === link.targetId,
    );
    if (duplicate) return existing;
    return [...existing, link];
}

export function removeWhiteboardLinkFromList(links, linkId) {
    return (Array.isArray(links) ? links : []).filter((link) => link.id !== linkId);
}

export function removeChartLinkToTarget(links, targetChartId) {
    return (Array.isArray(links) ? links : []).filter(
        (link) => !(link.type === WHITEBOARD_LINK_TYPES.CHART && link.targetId === targetChartId),
    );
}

export function linkTargetHref(link) {
    if (!link?.targetId) return null;
    if (link.type === WHITEBOARD_LINK_TYPES.CHART) {
        return `/charts/${link.targetId}`;
    }
    return `/products/${link.targetId}`;
}

export function linkedByLabel(link) {
    const linkedBy = link?.linkedBy;
    if (!linkedBy) return 'Unknown';
    return linkedBy.displayName || linkedBy.userId || 'Unknown';
}

const LINK_ADDED_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});

export function formatLinkAddedDate(link) {
    const raw = link?.linkedAt;
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return LINK_ADDED_DATE_FORMAT.format(date);
}
